import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/Brand";
import { PenLine, FileText, CheckSquare, Building2, CalendarDays, ArrowRight, Inbox } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="card p-5 flex items-center gap-4" data-testid={testid}>
      <div className="w-11 h-11 rounded-xl bg-sand flex items-center justify-center text-terracotta"><Icon size={20} /></div>
      <div>
        <div className="text-2xl font-black text-navy" data-testid={`${testid}-value`}>{value}</div>
        <div className="text-xs font-bold uppercase tracking-wider text-[#78716C]">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="dashboard-loading">Loading dashboard…</div>;

  return (
    <div className="space-y-8 max-w-6xl" data-testid={isAdmin ? "admin-dashboard" : "consultant-dashboard"}>
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">{isAdmin ? "CKingEducation Admin" : "Consultant workspace"}</p>
          <h1 className="section-title text-3xl sm:text-4xl mt-1">
            {isAdmin ? "Operations overview" : `Welcome, ${user?.name?.split(" ")[0]}`}
          </h1>
        </div>
        <Link to="/log-work" className="btn-primary" data-testid="log-todays-work-button">
          <PenLine size={16} /> Log Today's Work
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 fade-up-1">
        <Stat icon={CalendarDays} label="Service days this month" value={data.service_days_this_month} testid="stat-service-days" />
        <Stat icon={FileText} label="Logs today" value={data.todays_logs.length} testid="stat-logs-today" />
        {isAdmin
          ? <Stat icon={Inbox} label="Unreviewed logs" value={data.unreviewed_logs.length} testid="stat-unreviewed" />
          : <Stat icon={Building2} label="Assigned schools" value={data.assigned_schools.length} testid="stat-assigned-schools" />}
        <Stat icon={CheckSquare} label="Drafts awaiting review" value={data.pending_drafts.length} testid="stat-pending-drafts" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 fade-up-2">
        <div className="lg:col-span-2 card p-6" data-testid="recent-logs-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-lg text-navy">Recent daily logs</h2>
            <Link to="/logs" className="text-sm font-bold text-terracotta hover:underline flex items-center gap-1" data-testid="view-all-logs-link">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {data.recent_logs.length === 0 && (
            <p className="text-sm text-[#78716C]" data-testid="recent-logs-empty">No logs yet. Start with “Log Today's Work”.</p>
          )}
          <ul className="divide-y divide-[#F3EFE6]">
            {data.recent_logs.map((lg) => (
              <li key={lg.id}>
                <Link to={`/logs/${lg.id}`} className="flex items-center justify-between gap-3 py-3 hover:bg-canvas -mx-2 px-2 rounded-lg transition-colors"
                  data-testid={`recent-log-${lg.id}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-sm text-navy truncate">{lg.school_name || "—"} · {lg.service_type}</div>
                    <div className="text-xs text-[#78716C]">{lg.date}{isAdmin && ` · ${lg.consultant_name}`}</div>
                  </div>
                  <StatusBadge status={lg.status} />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-6">
          {isAdmin ? (
            <div className="card p-6" data-testid="unreviewed-card">
              <h2 className="font-serif font-bold text-lg text-navy mb-4">Needs your review</h2>
              {data.unreviewed_logs.length === 0 && <p className="text-sm text-[#78716C]">Queue is clear.</p>}
              <ul className="space-y-3">
                {data.unreviewed_logs.slice(0, 5).map((lg) => (
                  <li key={lg.id} className="flex items-center justify-between gap-3">
                    <Link to={`/logs/${lg.id}`} className="text-sm font-bold text-navy hover:text-terracotta truncate"
                      data-testid={`unreviewed-log-${lg.id}`}>
                      {lg.school_name} · {lg.date}
                    </Link>
                    <StatusBadge status={lg.status} />
                  </li>
                ))}
              </ul>
              <Link to="/review" className="btn-outline w-full mt-5" data-testid="open-review-queue-button">Open review queue</Link>
            </div>
          ) : (
            <div className="card p-6" data-testid="assigned-schools-card">
              <h2 className="font-serif font-bold text-lg text-navy mb-4">Your assigned schools</h2>
              {data.assigned_schools.length === 0 && <p className="text-sm text-[#78716C]">No schools assigned yet — ask an Admin.</p>}
              <ul className="space-y-2.5">
                {data.assigned_schools.map((s) => (
                  <li key={s.id} className="flex items-center gap-2.5 text-sm font-bold text-navy" data-testid={`assigned-school-${s.id}`}>
                    <Building2 size={15} className="text-terracotta shrink-0" /> {s.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="card p-6" data-testid="upcoming-days-card">
            <h2 className="font-serif font-bold text-lg text-navy mb-4">Upcoming service days</h2>
            {data.upcoming_days.length === 0 && <p className="text-sm text-[#78716C]">Nothing scheduled.</p>}
            <ul className="space-y-3">
              {data.upcoming_days.slice(0, 5).map((d) => (
                <li key={d.id} className="text-sm" data-testid={`upcoming-day-${d.id}`}>
                  <div className="font-bold text-navy">{d.school_name}</div>
                  <div className="text-xs text-[#78716C]">{d.date} · {d.service_type}{isAdmin && ` · ${d.consultant_name}`}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
